import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { GroupModel } from 'app/models/groupModel'
import { getCompaniesSimple } from 'app/actions/company'
import {
  GROUPS_KEY,
  addCompanyToGroupsRequest,
  addGroupRequest,
  deleteGroupsRequest,
  fetchGroups,
  removeCompanyFromGroupsRequest,
} from 'app/containers/administration/groupQueries'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminIcon } from '@/modules/shared/components'
import {
  clearAdminSelection,
  setAdminSelectedCount,
  subscribeToAdminRemove,
} from '@/modules/shared/stores/adminSelectionStore'
import { Button } from '@/ui/button'
import { Loader } from '@/ui'
import { toast } from '@/lib/toasts/api'
import { PickerList } from './components/PickerList'

type Props = {
  /** Panel otwarty w kontekście firmy zawęża listy do jej najemcy. */
  currentCompanyId?: string
}

const COMPANIES_KEY = ['admin', 'companies', 'simple'] as const

/**
 * Zakładka GRUPY FIRM panelu admina - funkcyjna, na react-query.
 *
 * Zastępuje `app/components/administration/companyGroups.tsx` (klasa, trzy definicje
 * kolumn react-table v6). Układ ten sam: grupy po lewej, firmy w grupie w środku,
 * pozostałe firmy po prawej, a strzałki między środkiem a prawą przenoszą zaznaczone.
 */
export const AdminCompanyGroupsPage: React.FC<Props> = ({
  currentCompanyId,
}) => {
  const { t } = useLocalize()
  const queryClient = useQueryClient()

  const groupsQuery = useQuery({
    queryKey: [...GROUPS_KEY, currentCompanyId ?? null],
    queryFn: () => fetchGroups(currentCompanyId),
  })
  const companiesQuery = useQuery({
    queryKey: [...COMPANIES_KEY, currentCompanyId ?? null],
    queryFn: () => getCompaniesSimple(currentCompanyId, false, false),
  })

  const groups: GroupModel[] = groupsQuery.data ?? []
  const companies: Array<{ id: number; companyName: string }> =
    companiesQuery.data ?? []

  const [selectedGroupId, setSelectedGroupId] = React.useState<number | null>(
    null,
  )
  const [groupsToDelete, setGroupsToDelete] = React.useState<number[]>([])
  const [checkedInGroup, setCheckedInGroup] = React.useState<number[]>([])
  const [checkedOutside, setCheckedOutside] = React.useState<number[]>([])
  const [newGroupName, setNewGroupName] = React.useState('')

  const selectedGroup = groups.find((group) => group.id === selectedGroupId)
  const inGroup = selectedGroup?.companies ?? []
  const inGroupIds = inGroup.map((company) => company.id)
  const outsideGroup = companies.filter(
    (company) => !inGroupIds.includes(company.id),
  )

  const reload = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: GROUPS_KEY })
  }, [queryClient])

  React.useEffect(() => {
    setAdminSelectedCount(groupsToDelete.length)
  }, [groupsToDelete])
  React.useEffect(() => () => clearAdminSelection(), [])

  const addGroupMutation = useMutation({ mutationFn: addGroupRequest })
  const deleteMutation = useMutation({
    mutationFn: (ids: number[]) =>
      deleteGroupsRequest(ids, currentCompanyId),
  })
  const assignMutation = useMutation({ mutationFn: addCompanyToGroupsRequest })
  const unassignMutation = useMutation({
    mutationFn: removeCompanyFromGroupsRequest,
  })

  const deleteRef = React.useRef<number[]>([])
  deleteRef.current = groupsToDelete
  React.useEffect(
    () =>
      subscribeToAdminRemove(async () => {
        const ids = deleteRef.current
        if (ids.length === 0) return
        await deleteMutation.mutateAsync(ids)
        setGroupsToDelete([])
        if (ids.includes(selectedGroupId as number)) setSelectedGroupId(null)
        await reload()
        clearAdminSelection()
        toast.success({
          title: t('administrationPanel-companies.groups-deleted-title'),
          text: t('administrationPanel-companies.groups-deleted'),
        })
      }),
    [deleteMutation, reload, selectedGroupId, t],
  )

  const addGroup = async () => {
    if (!newGroupName.trim()) return
    const created = await addGroupMutation.mutateAsync({
      name: newGroupName,
      tenantId: currentCompanyId,
    } as any)
    if ((created as GroupModel)?.error) {
      toast.error({
        title: t('administrationPanel-companyGroups.validation-name-empty'),
      })
      return
    }
    setNewGroupName('')
    await reload()
    toast.success({ title: t('administrationPanel-companyGroups.add-group') })
  }

  const assign = async () => {
    if (checkedOutside.length === 0 || selectedGroupId == null) return
    await assignMutation.mutateAsync({
      companyIds: checkedOutside,
      groupIds: [selectedGroupId],
      tenantId: currentCompanyId,
    } as any)
    setCheckedOutside([])
    await reload()
  }

  const unassign = async () => {
    if (checkedInGroup.length === 0 || selectedGroupId == null) return
    await unassignMutation.mutateAsync({
      companyGroups: checkedInGroup.map((companyId) => ({
        companyId,
        groupId: selectedGroupId,
      })),
      tenantId: currentCompanyId,
    } as any)
    setCheckedInGroup([])
    await reload()
  }

  const toggle = (
    setter: React.Dispatch<React.SetStateAction<number[]>>,
    id: number,
  ) =>
    setter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )

  if (groupsQuery.isLoading) {
    return (
      <div className="tw-flex tw-h-64 tw-items-center tw-justify-center">
        <Loader size="lg" />
      </div>
    )
  }

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-3 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
        <input
          className="tw-w-[280px] tw-rounded tw-border tw-border-base-300 tw-px-3 tw-py-1.5 tw-text-sm tw-outline-none focus:tw-border-primary"
          placeholder={t('administrationPanel-companyGroups.name')}
          value={newGroupName}
          onChange={(event) => setNewGroupName(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && addGroup()}
        />
        <Button
          variant="primary"
          size="sm"
          disabled={addGroupMutation.isPending}
          onClick={addGroup}
        >
          <AdminIcon name="add" className="white tw-h-4 tw-w-4" />
          {t('administrationPanel-companyGroups.add-group')}
        </Button>
      </div>

      {/* Panel admina nie ogranicza wysokości treści, więc bez własnej wysokości kolumny
          rozciągały się na całą listę firm, a strzałki między nimi lądowały poza ekranem. */}
      <div className="tw-flex tw-h-[calc(100vh-220px)] tw-gap-3 tw-overflow-hidden tw-p-3">
        <div className="tw-flex tw-min-h-0 tw-w-[320px] tw-flex-col">
          <PickerList
            idPrefix="group"
            title={t('administrationPanel-companyGroups.defined-groups')}
            items={groups.map((group) => ({
              id: group.id,
              label: group.name ?? '-',
            }))}
            checkedIds={groupsToDelete}
            onToggle={(id) => toggle(setGroupsToDelete, id as number)}
            activeId={selectedGroupId}
            onRowClick={(id) => {
              setSelectedGroupId(id as number)
              setCheckedInGroup([])
              setCheckedOutside([])
            }}
          />
        </div>

        {selectedGroupId != null && (
          <>
            <PickerList
              idPrefix="in-group"
              title={`${t('administrationPanel-companyGroups.selected-groups')} ${selectedGroup?.name ?? ''}`}
              items={inGroup.map((company) => ({
                id: company.id,
                label: company.companyName,
              }))}
              checkedIds={checkedInGroup}
              onToggle={(id) => toggle(setCheckedInGroup, id as number)}
            />

            <div className="tw-flex tw-flex-col tw-justify-center tw-gap-2">
              <Button
                variant="primary"
                mode="outline"
                size="sm"
                disabled={checkedOutside.length === 0}
                title="addCompanyToGroup"
                onClick={assign}
              >
                <AdminIcon name="double-arrow-left" />
              </Button>
              <Button
                variant="primary"
                mode="outline"
                size="sm"
                disabled={checkedInGroup.length === 0}
                title="removeCompanyFromGroup"
                onClick={unassign}
              >
                <AdminIcon name="double-arrow-right" />
              </Button>
            </div>

            <PickerList
              idPrefix="outside"
              title={t('administrationPanel-companyGroups.all-groups')}
              items={outsideGroup.map((company) => ({
                id: company.id,
                label: company.companyName,
              }))}
              checkedIds={checkedOutside}
              onToggle={(id) => toggle(setCheckedOutside, id as number)}
            />
          </>
        )}
      </div>
    </div>
  )
}
